import { Check, Lock, Clock } from 'lucide-react'

/**
 * RoundLockBadge — per-round state chip for multi-week brackets.
 *
 * A multi-week bracket (MLP playoffs) opens one round at a time: users pick the
 * open round, the round locks at its own deadline, the admin enters that
 * round's official results, and the next round opens once the league announces
 * its matchups. This chip marks the rounds you CAN'T pick yet.
 *
 * Renders nothing for a legacy bracket (lockInfo is null — the single
 * bracket-level status already says everything), and nothing for the open
 * round, which should look like any ordinary bracket round.
 *
 * @param {object|null} lockInfo - from useBracketStore.getRoundLockInfo(round)
 * @param {boolean} isEditingOfficial - admin entering results (flips the
 *   'locked' copy from "you can't pick" to "you can enter results")
 */
export default function RoundLockBadge({ lockInfo, isEditingOfficial = false, className = '' }) {
  if (!lockInfo) return null

  const { status, matchupsPublished } = lockInfo

  const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-tight'

  // Open round: no chip. The deadline is already stated once, in the banner at
  // the top of the tournament page; repeating it beside the round heading
  // crowded the header and looked nothing like a normal bracket, where an open
  // round carries no chip at all. Only the not-yet-pickable rounds need a mark.
  if (status === 'open') return null

  if (status === 'locked') {
    return (
      <span className={`${base} bg-[var(--mauve-4)] text-muted-foreground ${className}`}>
        {isEditingOfficial
          ? <><Check className="h-3 w-3 shrink-0" aria-hidden="true" /><span>Enter results</span></>
          : <><Lock className="h-3 w-3 shrink-0" aria-hidden="true" /><span>Locked</span></>}
      </span>
    )
  }

  // pending — the round exists on the bracket but isn't open yet. Distinguish
  // "we know who plays whom, picks just aren't open" from "the league hasn't
  // announced the matchups", since for the quarterfinals and semifinals the
  // matchups genuinely don't exist until the bye teams choose their opponents.
  return (
    <span className={`${base} bg-[var(--mauve-3)] text-muted-foreground ${className}`}>
      <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{matchupsPublished ? 'Opens soon' : 'Matchups TBD'}</span>
    </span>
  )
}
